import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { useAuth } from '../hooks/useAuth'

const PermissionsContext = createContext(null)

// Role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY = {
  owner: 100,
  manager: 90,
  captain: 80,
  chief_engineer: 60,
  chief_steward: 60,
  engineer: 40,
  steward: 40,
  deckhand: 20,
  crew: 20,
}

// All available roles for display
export const ROLES = [
  { value: 'owner', label: 'Owner', level: 100 },
  { value: 'manager', label: 'Manager', level: 90 },
  { value: 'captain', label: 'Captain', level: 80 },
  { value: 'chief_engineer', label: 'Chief Engineer', level: 60 },
  { value: 'chief_steward', label: 'Chief Steward', level: 60 },
  { value: 'engineer', label: 'Engineer', level: 40 },
  { value: 'steward', label: 'Steward', level: 40 },
  { value: 'deckhand', label: 'Deckhand', level: 20 },
  { value: 'crew', label: 'Crew', level: 20 },
]

// Permission definitions by feature
export const PERMISSIONS = {
  // Team Management
  'team.view': { minRole: 'crew', description: 'View team members' },
  'team.invite': { minRole: 'captain', description: 'Invite new crew members' },
  'team.edit': { minRole: 'captain', description: 'Edit crew details' },
  'team.remove': { minRole: 'captain', description: 'Remove crew members' },
  'team.manage_roles': { minRole: 'owner', description: 'Change crew roles' },

  // Invoices & Finances
  'invoices.view': { minRole: 'chief_steward', description: 'View invoices' },
  'invoices.create': { minRole: 'chief_steward', description: 'Create invoices' },
  'invoices.edit': { minRole: 'chief_steward', description: 'Edit invoices' },
  'invoices.delete': { minRole: 'captain', description: 'Delete invoices' },
  'invoices.approve': { minRole: 'captain', description: 'Approve/pay invoices' },

  // Documents
  'documents.view': { minRole: 'crew', description: 'View documents' },
  'documents.upload': { minRole: 'steward', description: 'Upload documents' },
  'documents.edit': { minRole: 'chief_steward', description: 'Edit documents' },
  'documents.delete': { minRole: 'captain', description: 'Delete documents' },

  // Guest Cardex
  'guests.view': { minRole: 'steward', description: 'View guest info' },
  'guests.create': { minRole: 'steward', description: 'Add guests' },
  'guests.edit': { minRole: 'steward', description: 'Edit guest details' },
  'guests.delete': { minRole: 'chief_steward', description: 'Delete guests' },
  'guests.view_preferences': { minRole: 'steward', description: 'View guest preferences' },

  // Essentials / Inventory
  'inventory.view': { minRole: 'crew', description: 'View inventory' },
  'inventory.update': { minRole: 'steward', description: 'Update quantities' },
  'inventory.create': { minRole: 'chief_steward', description: 'Add new items' },
  'inventory.delete': { minRole: 'chief_steward', description: 'Delete items' },

  // Orders
  'orders.view': { minRole: 'steward', description: 'View orders' },
  'orders.create': { minRole: 'steward', description: 'Create orders' },
  'orders.edit': { minRole: 'steward', description: 'Edit orders' },
  'orders.approve': { minRole: 'chief_steward', description: 'Approve orders' },
  'orders.delete': { minRole: 'captain', description: 'Delete orders' },

  // Maintenance
  'maintenance.view': { minRole: 'crew', description: 'View maintenance tasks' },
  'maintenance.create': { minRole: 'engineer', description: 'Create tasks' },
  'maintenance.edit': { minRole: 'engineer', description: 'Edit tasks' },
  'maintenance.complete': { minRole: 'engineer', description: 'Mark as complete' },
  'maintenance.delete': { minRole: 'chief_engineer', description: 'Delete tasks' },

  // Equipment
  'equipment.view': { minRole: 'crew', description: 'View equipment' },
  'equipment.create': { minRole: 'chief_engineer', description: 'Add equipment' },
  'equipment.edit': { minRole: 'engineer', description: 'Edit equipment' },
  'equipment.delete': { minRole: 'chief_engineer', description: 'Delete equipment' },

  // Issue Reports
  'issues.view': { minRole: 'crew', description: 'View issues' },
  'issues.create': { minRole: 'crew', description: 'Report issues' },
  'issues.edit': { minRole: 'engineer', description: 'Edit issues' },
  'issues.resolve': { minRole: 'engineer', description: 'Resolve issues' },
  'issues.delete': { minRole: 'chief_engineer', description: 'Delete issues' },

  // Schedule
  'schedule.view': { minRole: 'crew', description: 'View schedule' },
  'schedule.edit': { minRole: 'captain', description: 'Edit schedule' },

  // Bookings / Charter
  'bookings.view': { minRole: 'steward', description: 'View bookings' },
  'bookings.create': { minRole: 'captain', description: 'Create bookings' },
  'bookings.edit': { minRole: 'captain', description: 'Edit bookings' },
  'bookings.delete': { minRole: 'captain', description: 'Delete bookings' },

  // Handovers
  'handovers.view': { minRole: 'crew', description: 'View handovers' },
  'handovers.create': { minRole: 'chief_steward', description: 'Create handovers' },
  'handovers.edit': { minRole: 'steward', description: 'Complete checklist items' },
  'handovers.complete': { minRole: 'captain', description: 'Complete handover' },

  // Reports
  'reports.view': { minRole: 'chief_steward', description: 'View reports' },
  'reports.create': { minRole: 'captain', description: 'Generate reports' },
  'reports.send': { minRole: 'captain', description: 'Send reports to owner' },

  // Settings
  'settings.view': { minRole: 'crew', description: 'View settings' },
  'settings.edit_profile': { minRole: 'crew', description: 'Edit own profile' },
  'settings.edit_yacht': { minRole: 'captain', description: 'Edit yacht settings' },
  'settings.manage_integrations': { minRole: 'owner', description: 'Manage integrations' },
  'settings.billing': { minRole: 'owner', description: 'Manage billing' },
}

// Department groupings for UI
export const DEPARTMENTS = {
  deck: ['captain', 'deckhand'],
  engineering: ['chief_engineer', 'engineer'],
  interior: ['chief_steward', 'steward'],
  management: ['owner'],
}

export function PermissionsProvider({ children }) {
  const { profile } = useAuth()
  const [currentYacht, setCurrentYacht] = useState(null)
  const [userYachtRole, setUserYachtRole] = useState(null)
  const [loading, setLoading] = useState(true)

  // Get user's role for the current yacht
  useEffect(() => {
    if (profile && currentYacht) {
      // In a real app, fetch from yacht_crew table
      // For now, use the profile's default role (default to owner for yacht creators)
      const role = profile.role || 'owner'
      console.log('[PERMISSIONS] Setting role from profile (with yacht):', role, 'Profile role:', profile.role)
      setUserYachtRole(role)
      setLoading(false)
    } else if (profile) {
      // Default to profile role if no yacht selected
      const role = profile.role || 'owner'
      console.log('[PERMISSIONS] Setting role from profile (no yacht):', role, 'Profile role:', profile.role)
      setUserYachtRole(role)
      setLoading(false)
    } else {
      // No profile yet - default to owner to prevent buttons from hiding during load
      console.log('[PERMISSIONS] No profile yet, defaulting to owner')
      setUserYachtRole('owner')
      setLoading(false)
    }
  }, [profile, currentYacht])

  // Check if user's role meets minimum requirement
  const hasRoleLevel = (minRole) => {
    if (!userYachtRole) return false
    const userLevel = ROLE_HIERARCHY[userYachtRole] || 0
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0
    return userLevel >= requiredLevel
  }

  // Check specific permission
  const hasPermission = (permission) => {
    const permConfig = PERMISSIONS[permission]
    if (!permConfig) {
      console.warn(`Unknown permission: ${permission}`)
      return false
    }
    const result = hasRoleLevel(permConfig.minRole)
    console.log(`[PERMISSIONS] Check "${permission}" (min: ${permConfig.minRole}) for role "${userYachtRole}": ${result}`)
    return result
  }

  // Check multiple permissions (all must pass)
  const hasAllPermissions = (permissions) => {
    return permissions.every(p => hasPermission(p))
  }

  // Check multiple permissions (any must pass)
  const hasAnyPermission = (permissions) => {
    return permissions.some(p => hasPermission(p))
  }

  // Check if user is owner
  const isOwner = () => userYachtRole === 'owner'

  // Check if user is captain or above
  const isCaptainOrAbove = () => hasRoleLevel('captain')

  // Check if user is chief (chief_engineer or chief_steward) or above
  const isChiefOrAbove = () => hasRoleLevel('chief_engineer')

  // Get list of roles user can assign (can only assign roles below their level)
  const assignableRoles = useMemo(() => {
    if (!userYachtRole) return []
    const userLevel = ROLE_HIERARCHY[userYachtRole] || 0
    return ROLES.filter(role => role.level < userLevel)
  }, [userYachtRole])

  const value = {
    // State
    currentYacht,
    setCurrentYacht,
    userYachtRole,
    loading,

    // Permission checks
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRoleLevel,

    // Convenience checks
    isOwner,
    isCaptainOrAbove,
    isChiefOrAbove,

    // Role helpers
    assignableRoles,

    // Constants
    ROLES,
    PERMISSIONS,
    ROLE_HIERARCHY,
  }

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

export function usePermissions() {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
